import React, { useState } from "react";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import axios from "axios";
import { ServiceAPI } from "../../Api/ServiceAPI";
import "./NalogAdmin.css";

const NalogAdmin = () => {
  const [pastedJson, setPastedJson] = useState("");
  const [currentDate, setCurrentDate] = useState("");
  const [selectedLocation, setSelectedLocation] = useState("cco");
  const [reportData, setReportData] = useState([]);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const [customTime, setCustomTime] = useState("");

  const parseDateFromKey = (dateKey) => {
    if (!dateKey) return "";
    return dateKey.split(" ")[0].replace(/\./g, "-");
  };
  const parseTimeFromKey = (dateKey) => {
    if (!dateKey) return "";
    const parts = dateKey.split(" ");
    if (parts.length > 1) {
      return parts[1];
    }
    return "";
  };

  const getCurrentTime = () => {
    const now = new Date();
    const hh = String(now.getHours()).padStart(2, "0");
    const mm = String(now.getMinutes()).padStart(2, "0");
    const ss = String(now.getSeconds()).padStart(2, "0");
    return `${hh}:${mm}:${ss}`;
  };
  const getFinalTime = () => {
    if (customTime.trim()) {
      return customTime.trim();
    }
    const fromJson = parseTimeFromKey(currentDate);
    return fromJson || getCurrentTime();
  };

  const handleLoadJson = () => {
    try {
      const data = JSON.parse(pastedJson);

      const dateKey = Object.keys(data)[0];
      setCurrentDate(dateKey);

      const locationKey = Object.keys(data[dateKey])[0];
      setSelectedLocation(locationKey.toLowerCase());

      const initialData = data[dateKey][locationKey].map((item) => ({
        ident: item.Ident,
        izvor: item.Izvor,
        imeRibe: item["Ime ribe"],
        preporuka: item["Preporuka(kg)"],
      }));
      setReportData(initialData);
    } catch (error) {
      alert("Neispravan JSON format ili struktura!");
      console.error(error);
    }
  };

  const handleInputChange = (index, field, value) => {
    const updatedData = [...reportData];
    const updatedErrors = { ...errors };

    if (field === "preporuka") {
      // Convert comma to dot
      value = value.replace(",", ".");
      if (value !== "" && (isNaN(value) || parseFloat(value) < 0)) {
        updatedErrors[`${index}-preporuka`] =
          "Unesite valjani broj (0 ili više).";
      } else {
        delete updatedErrors[`${index}-preporuka`];
      }
    }

    updatedData[index][field] = value;
    setReportData(updatedData);
    setErrors(updatedErrors);
  };

  // Prevent invalid chars, handle "Enter" -> next row
  const handleKeyPress = (e, index) => {
    const regex = /^[0-9.,]*$/;
    if (e.target.name === "preporuka" && !regex.test(e.key)) {
      e.preventDefault();
    }
    if (e.key === "Enter") {
      e.preventDefault();
      const nextIndex = index + 1;
      if (nextIndex < reportData.length) {
        document.getElementById(`preporuka-${nextIndex}`)?.focus();
      } else {
        e.target.blur();
      }
    }
  };

  // Delete a row
  const handleDeleteRow = (index) => {
    const updatedData = [...reportData];
    updatedData.splice(index, 1);
    setReportData(updatedData);
  };

  // Add a row
  const handleAddRow = () => {
    const newRow = {
      ident: "",
      izvor: "",
      imeRibe: "",
      preporuka: "",
    };
    setReportData([...reportData, newRow]);
  };
  const handleSave = async () => {
    const newErrors = {};
    reportData.forEach((item, index) => {
      if (item.preporuka === "" || item.preporuka === undefined) {
        newErrors[`${index}-preporuka`] =
          "Molimo ispunite polje Preporuka (kg).";
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      alert("Molimo ispunite sva obavezna polja prije spremanja.");
      return;
    }

    setSaving(true);
    setSaveSuccess(false);
    try {
      await generatePDF(reportData);
      setSaveSuccess(true);
    } catch (error) {
      console.error("Spremanje nije uspjelo:", error);
    } finally {
      setSaving(false);
      setTimeout(() => {
        setSaveSuccess(false);
      }, 4000);
    }
  };

  const generatePDF = (data) => {
    const doc = new jsPDF();
    const darkPurple = "#6A0DAD";
    const lightPurple = "#E6E6FA";
    const pageWidth = doc.internal.pageSize.getWidth();

    const safeDate = parseDateFromKey(currentDate);
    const finalTime = getFinalTime();

    doc.setFontSize(12);
    doc.setTextColor("#000000");
    doc.text(`Datum: ${safeDate}`, 14, 10);
    doc.text(`Vrijeme generiranja: ${finalTime}`, 14, 16);

    const fishPrediction = "Nalog BlueDataB";
    const fishPredictionWidth = doc.getTextWidth(fishPrediction);
    doc.text(fishPrediction, pageWidth - fishPredictionWidth - 14, 10);

    doc.setFontSize(36);
    doc.setTextColor(darkPurple);
    const locationText = selectedLocation.toUpperCase();
    const locationTextWidth = doc.getTextWidth(locationText);
    doc.text(locationText, pageWidth - locationTextWidth - 14, 30);

    doc.setDrawColor(150);
    doc.line(14, 36, pageWidth - 14, 36);

    const tableData = data.map((item) => [
      item.ident || "",
      item.izvor || "",
      item.imeRibe || "",
      item.preporuka !== "" ? item.preporuka : "N/A",
    ]);

    autoTable(doc, {
      startY: 42,
      head: [["Ident", "Izvor", "Ime ribe", "Preporuka (kg)"]],
      body: tableData,
      headStyles: {
        fillColor: darkPurple,
        textColor: "#ffffff",
        halign: "right",
      },
      bodyStyles: {
        fillColor: "#ffffff",
        textColor: "#000000",
        halign: "right",
      },
      alternateRowStyles: { fillColor: lightPurple },
      styles: { fontSize: 10, halign: "right" },
      margin: { left: 14, right: 14 },
    });

    const pdfBlob = doc.output("blob");
    const sanitizedTime = finalTime.replace(/:/g, "_");
    const fileName = `report_${selectedLocation}_${safeDate}_time_${sanitizedTime}.pdf`;

    return uploadToCloud(pdfBlob, fileName);
  };

  const uploadToCloud = async (fileBlob, fileName) => {
    try {
      const token = localStorage.getItem("token") || sessionStorage.getItem("token");
      axios.defaults.headers.common["user_authorization"] = `Bearer ${token}`;

      const accessTokenResponse = await ServiceAPI.login();
      if (accessTokenResponse.data.result === 0) {
        const _accessToken = accessTokenResponse.data.auth;
        const folderId = 15581081188;
        await ServiceAPI.upload_file(
          _accessToken,
          folderId,
          fileBlob,
          fileName,
          () => {}
        );
        return true;
      } else {
        throw new Error("Authentication failed.");
      }
    } catch (error) {
      console.error("Upload failed:", error);
      alert("Imate problema s vašom internet konekcijom. Molimo pokušajte ponovno.");
      throw error;
    }
  };

  return (
    <div className="nalog-container">
      <div className="json-container">
        <h2>Zalijepi JSON ovdje:</h2>
        <textarea
          className="json-textarea"
          value={pastedJson}
          onChange={(e) => setPastedJson(e.target.value)}
          rows={6}
        />
        <button className="load-json-button" onClick={handleLoadJson}>
          Učitaj JSON
        </button>
      </div>

      <div className="header-container">
        <div className="header-info">
          <h1 className="location-title">{selectedLocation}</h1>
          <div className="date-info">Datum (iz JSON-a): {currentDate}</div>
          <div className="custom-time-input">
            <label>Vrijeme generiranja (custom): </label>
            <input
              type="text"
              placeholder="HH:MM:SS"
              value={customTime}
              onChange={(e) => setCustomTime(e.target.value)}
            />
          </div>
        </div>

        <select
          className="location-select"
          value={selectedLocation}
          onChange={(e) => setSelectedLocation(e.target.value)}
        >
          {[
            "solin",
            "podstrana",
            "gomilica",
            "omis",
            "poljud",
            "mall",
            "belvedere",
            "kaufland",
            "Test",
            "brda",
            "brela",
            "cco",
          ].map((opt) => (
            <option key={opt} value={opt}>
              {opt.toUpperCase()}
            </option>
          ))}
        </select>
      </div>

      <table className="report-table">
        <thead>
          <tr className="table-header">
            <th className="table-cell">Ident</th>
            <th className="table-cell">Izvor</th>
            <th className="table-cell">Ime ribe</th>
            <th className="table-cell">Preporuka (kg)</th>
            <th className="table-cell">Akcija</th>
          </tr>
        </thead>
        <tbody>
          {reportData.map((item, index) => (
            <tr
              key={index}
              className={index % 2 === 0 ? "table-row-even" : "table-row-odd"}
            >
              <td className="table-cell">
                <input
                  type="text"
                  className="table-input"
                  value={item.ident}
                  onChange={(e) =>
                    handleInputChange(index, "ident", e.target.value)
                  }
                />
              </td>
              <td className="table-cell">
                <input
                  type="text"
                  className="table-input"
                  value={item.izvor}
                  onChange={(e) =>
                    handleInputChange(index, "izvor", e.target.value)
                  }
                />
              </td>
              <td className="table-cell">
                <input
                  type="text"
                  className="table-input"
                  value={item.imeRibe}
                  onChange={(e) =>
                    handleInputChange(index, "imeRibe", e.target.value)
                  }
                />
              </td>
              <td className="table-cell">
                <input
                  name="preporuka"
                  id={`preporuka-${index}`}
                  type="text"
                  className="table-input-right"
                  value={item.preporuka}
                  onChange={(e) =>
                    handleInputChange(index, "preporuka", e.target.value)
                  }
                  onKeyPress={(evt) => handleKeyPress(evt, index)}
                />
                {errors[`${index}-preporuka`] && (
                  <div className="error-text">
                    {errors[`${index}-preporuka`]}
                  </div>
                )}
              </td>
              <td className="table-cell">
                <button
                  className="delete-button"
                  onClick={() => handleDeleteRow(index)}
                >
                  Izbriši
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <button className="add-row-button" onClick={handleAddRow}>
        Dodaj red
      </button>

      {saveSuccess && (
        <div className="success-message">
          Vaša datoteka je uspješno spremljena ✅
        </div>
      )}

      <button className="save-button" onClick={handleSave} disabled={saving}>
        {saving ? "Spremanje..." : "Spremi"}
      </button>
    </div>
  );
};

export default NalogAdmin;
